import { useState, useEffect, useRef } from 'react';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { getUserAvatar } from '../../../utils/avatar.js';
import { Icon } from '../../Icon.js';
import { ReactionBubble, ReactionPicker } from '../../ReactionUI.js';
import { Opponents } from '../Opponents.js';
import { MyHand } from '../MyHand.js';
import { MuushigTable } from './MuushigTable.js';
import { MuushigControls } from './MuushigControls.js';
import { PauseOverlay } from '../common/PauseOverlay.js';
import { TurnIndicator } from '../common/TurnIndicator.js';
import { playCardSelectSound, playYourTurnSound, playTimerRedTick } from '../../../sound.js';
import { canFoldMuushig, legalMuushigCards, sortMuushigHand } from '@big2/shared';
import { TableInteractionsProvider } from '../TableInteractions.js';

export function MuushigGameScreen({ onOpenProfile }: { onOpenProfile?: () => void }) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { game, reactions } = state;

  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const prevTurnPlayerRef = useRef<string | null>(null);
  const lastRedTickSecRef = useRef<number | null>(null);

  // Clear selection if hand, stage, or pause status changes
  useEffect(() => {
    setSelectedCards(new Set());
  }, [game?.myHand.length, game?.muushig?.stage, game?.pause?.status]);

  const isMyTurn = !!game && game.activePlayerId === game.myPlayerId;
  const muushig = game?.muushig ?? null;
  const pause = game?.pause ?? null;
  const isPaused = pause?.status === 'paused';
  const isRequestingPause = pause?.status === 'requesting';

  // Play chime when it becomes our turn
  useEffect(() => {
    if (!game || isPaused) return;
    if (game.activePlayerId !== prevTurnPlayerRef.current) {
      prevTurnPlayerRef.current = game.activePlayerId;
      lastRedTickSecRef.current = null;
      if (game.activePlayerId === game.myPlayerId) {
        playYourTurnSound();
      }
    }
  }, [game?.activePlayerId, game?.myPlayerId, isPaused]);

  // Timer rendering + red timer warning sound
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!game?.timeoutEndsAt || isPaused) {
      setTimeLeft(null);
      lastRedTickSecRef.current = null;
      return;
    }
    const tick = () => {
      const serverNow = Date.now() + (state.serverOffsetMs ?? 0);
      const remain = Math.max(0, game.timeoutEndsAt! - serverNow);
      setTimeLeft(remain);
      if (game.activePlayerId === game.myPlayerId && remain > 0 && remain <= 5000) {
        const sec = Math.ceil(remain / 1000);
        if (lastRedTickSecRef.current !== sec) {
          lastRedTickSecRef.current = sec;
          playTimerRedTick(sec);
        }
      }
    };
    tick();
    const iv = setInterval(tick, 100);
    return () => clearInterval(iv);
  }, [game?.timeoutEndsAt, game?.activePlayerId, game?.myPlayerId, isPaused, state.serverOffsetMs]);

  if (!game || !muushig) return null;

  const myPlayerObj = game.players.find((p) => p.playerId === game.myPlayerId);
  const isFolded = !!myPlayerObj?.folded;

  const canFold =
    muushig.stage === 'decision' &&
    canFoldMuushig(game.players.map((player) => !!player.folded));

  const toggleCard = (card: string) => {
    if (isPaused || muushig.stage === 'decision' || isFolded) return;

    if (muushig.stage === 'playing' || muushig.stage === 'trump-exchange') {
      // Single-card selection in Muushig trick-taking or dealer trump exchange
      const next = new Set<string>();
      if (!selectedCards.has(card)) {
        next.add(card);
        playCardSelectSound(true);
      } else {
        playCardSelectSound(false);
      }
      setSelectedCards(next);
      return;
    }

    const maxSelectable = muushig.stage === 'exchange' ? Math.min(5, muushig.stockCount) : 5;

    const next = new Set(selectedCards);
    if (next.has(card)) {
      next.delete(card);
      playCardSelectSound(false);
    } else if (next.size < maxSelectable) {
      next.add(card);
      playCardSelectSound(true);
    }
    setSelectedCards(next);
  };

  const selectedArray = Array.from(selectedCards);

  const activeCount = game.players.filter((player) => !player.folded).length;
  const legalCards = isMyTurn && muushig.stage === 'playing'
    ? legalMuushigCards(game.myHand, muushig.trumpSuit, muushig.trickCards, activeCount)
    : [];
  const legalCardSet = new Set(legalCards);

  let playError: string | null = null;
  if (isMyTurn) {
    if (muushig.stage === 'trump-exchange') {
      if (selectedArray.length > 1) {
        playError = isMn
          ? 'Газрын модоор солих дээд тал нь 1 хөзөр сонгоно'
          : 'Select at most 1 card to replace with the pot card';
      }
    } else if (muushig.stage === 'exchange' && selectedArray.length > muushig.stockCount) {
      playError = isMn ? 'Газрын мод хүрэлцэхгүй' : 'Not enough stock cards';
    } else if (muushig.stage === 'playing') {
      if (selectedArray.length !== 1) {
        playError = isMn ? 'Гаргах 1 хөзрөө сонгоно уу' : 'Select 1 card to play';
      } else if (!legalCardSet.has(selectedArray[0])) {
        playError = isMn
          ? 'Өнгө дагаж, боломжтой бол ахална. Тамганы дүрмийг баримтална.'
          : 'Follow suit, trump if void, and beat when possible. Observe the ace rule.';
      }
    }
  }

  const handlePlay = () => {
    if (isMyTurn && !playError && !isPaused) {
      actions.playCards(selectedArray);
    }
  };

  const handlePass = () => {
    if (isMyTurn && canFold && !isPaused) {
      actions.passTurn();
    }
  };

  const handleClearSelection = () => {
    setSelectedCards(new Set());
  };

  // Muushig ordering: cards matching the pot card (trump suit) first top to bottom (desc),
  // and the rest ordered descending left to right.
  const sortedHand = sortMuushigHand(game.myHand, muushig.trumpSuit);

  const activePlayerName = game.players.find((p) => p.playerId === game.activePlayerId)?.name ?? '';
  const myReaction = reactions[game.myPlayerId];
  const myPlayerName = myPlayerObj?.name ?? '';
  const myAvatar = getUserAvatar(state.profile?.id || game.myPlayerId, myPlayerName);
  const myTotalScore = game.totals?.[game.myPlayerId] ?? muushig.scores?.[game.myPlayerId] ?? 15;
  const myTricksWon = myPlayerObj?.tricksWon ?? 0;

  const orderIndex = (game.mySeat - muushig.dealerSeat + game.players.length) % game.players.length;

  let stagePrompt: string | null = null;
  if (muushig.stage === 'decision') {
    stagePrompt = isMyTurn
      ? isMn ? 'Энэ өрөгт орох эсвэл өнжихөө сонгоно уу' : 'Choose whether to stay in this round or sit out'
      : isMn ? `${activePlayerName} шийдвэрээ гаргаж байна...` : `Waiting for ${activePlayerName} to decide...`;
  } else if (muushig.stage === 'exchange') {
    const maxSelect = Math.min(5, muushig.stockCount);
    stagePrompt = isMyTurn
      ? muushig.stockCount === 0
        ? isMn ? 'Газрын мод дууссан (0 үлдсэн). "Хэвээр үлдээх" дарж үргэлжлүүлнэ үү' : 'Stock empty (0 left). Click "Keep Hand" to continue'
        : isMn
        ? `Газарт ${muushig.stockCount} мод байна • Та 0-${maxSelect} мод сонгож солих боломжтой`
        : `Stock has ${muushig.stockCount} cards • Select 0 to ${maxSelect} cards to swap`
      : isMn ? `${activePlayerName} хөзөр сольж байна... (Газарт ${muushig.stockCount} мод)` : `Waiting for ${activePlayerName} to exchange... (${muushig.stockCount} cards left)`;
  } else if (muushig.stage === 'trump-exchange') {
    const isDealer = game.mySeat === muushig.dealerSeat;
    stagePrompt = isDealer
      ? isMn ? 'Газрын хөзрийг авах 1 модоо сонгох эсвэл хэвээр үлдээнэ үү' : 'Select 1 card to swap for trump, or keep hand'
      : isMn ? `Тараагч (${activePlayerName}) хөзөр авах эсэхээ шийдэж байна...` : `Waiting for dealer (${activePlayerName}) to pick up trump...`;
  } else if (muushig.stage === 'playing') {
    if (isFolded) {
      stagePrompt = isMn ? 'Та өнжсөн байна. Бусдын тоглолтыг ажиглаж байна' : 'Sitting out this round. Spectating match';
    } else if (isMyTurn) {
      if (muushig.trickCards.length === 0) {
        stagePrompt = game.myHand.includes('A' + muushig.trumpSuit)
          ? isMn ? 'Та хөзрийн тамгаар гарах үүрэгтэй' : 'Compulsory: lead the trump Ace'
          : isMn ? 'Эхний хөзрийг гаргана уу' : 'Lead a card into trick';
      } else {
        stagePrompt = isMn ? 'Өнгө дагаж, боломжтой бол ахална' : 'Follow suit & beat if possible';
      }
    } else {
      stagePrompt = isMn ? `${activePlayerName}-н ээлж` : `${activePlayerName}'s turn`;
    }
  }

  const emptyNotice = isFolded
    ? isMn
      ? 'Та энэ өрөгт өнжсөн байна (0 оноо). Дараагийн өргийг хүлээнэ үү...'
      : 'You sat out this round (0 pts). Waiting for next round...'
    : undefined;

  const isDealer = game.mySeat === muushig.dealerSeat;
  const canExchangeStock = isMyTurn && muushig.stage === 'exchange' && !isPaused && selectedArray.length <= muushig.stockCount;
  const canTakeTrump = isMyTurn && isDealer && muushig.stage === 'trump-exchange' && !isPaused;

  const handleTakeTrumpFromTable = () => {
    if (!canTakeTrump) return;
    if (selectedArray.length === 1) {
      actions.playCards(selectedArray);
    } else if (selectedArray.length === 0) {
      // Pick the lowest non-trump card in hand as default recommendation for dealer
      const myHand = game.myHand ?? [];
      if (myHand.length > 0) {
        const nonTrump = myHand.find((c) => c[1] !== muushig.trumpSuit);
        const cardToPick = nonTrump ?? myHand[0];
        setSelectedCards(new Set([cardToPick]));
      }
    }
  };

  const handleExchangeStockFromTable = () => {
    if (!canExchangeStock) return;
    actions.playCards(selectedArray);
  };

  const isFinished = game.phase === 'finished';
  const isHost = Boolean(state.lobby && state.lobby.hostId === state.playerId);
  const matchOver = Boolean(state.lastGameOver?.matchOver);
  const hasTopOpponent = game.players.length === 2 || game.players.length === 4 || game.players.length === 5;

  return (
    <TableInteractionsProvider onOpenProfile={onOpenProfile}>
      <div className={`game-screen${isMyTurn ? ' my-turn' : ''}`}>
        {/* Table Area */}
        <div className={`game-table-area is-muushig${hasTopOpponent ? ' has-top-opponent' : ''}`}>
          <Opponents />
          <PauseOverlay />
          <MuushigTable
            isPaused={isPaused}
            canExchangeStock={canExchangeStock}
            canTakeTrump={canTakeTrump}
            onExchangeStock={handleExchangeStockFromTable}
            onTakeTrump={handleTakeTrumpFromTable}
          />

        {/* Local Player Floating Reaction Bubble */}
        {myReaction && (
          <div className="my-reaction-slot">
            <ReactionBubble
              reactionId={myReaction.reactionId}
              seq={myReaction.seq}
              playerName={myPlayerName}
            />
          </div>
        )}

        <TurnIndicator
          isMyTurn={isMyTurn}
          timeLeft={timeLeft}
          customPrompt={
            isMyTurn
              ? (playError ? `⚠️ ${playError}` : stagePrompt ? `🎯 ${stagePrompt}` : undefined)
              : undefined
          }
        />
      </div>

      {/* Player Area */}
      <div className="player-area">
        <div className="sr-only">
          <span role="status">
            {isPaused
              ? isMn ? 'Тоглоом түр зогссон' : 'Game paused'
              : isFolded
              ? isMn ? 'Өнжсөн' : 'Sitting out'
              : isMyTurn
              ? t.yourTurn
              : t.waitingForTurn(activePlayerName)}
          </span>
        </div>

        {/* Muushig Controls */}
        <MuushigControls
          isMyTurn={isMyTurn}
          isPaused={isPaused}
          stage={muushig.stage}
          canFold={canFold}
          selectedCount={selectedArray.length}
          stockCount={muushig.stockCount}
          playError={playError}
          onPass={handlePass}
          onPlay={handlePlay}
          onClearSelection={handleClearSelection}
          isFinished={isFinished}
          isHost={isHost}
          matchOver={matchOver}
          onPlayAgain={() => actions.startGame({ restartMatch: false })}
          onRestartMatch={() => actions.startGame({ restartMatch: true })}
          onReturnToLobby={() => {
            if (isHost) actions.returnToLobby();
            actions.clearGameOver();
          }}
        />

        {/* Above cards: Main player's centered status bar with Muushig Score */}
        <div className="hand-header-bar">
          <div
            className={`self-counter${state.profile?.tier === 'paid' ? ' is-vip' : ''}`}
            data-seat={game?.mySeat}
            title={isMyTurn ? t.yourTurn : myPlayerName}
          >
            <button
              className="icon-button pause-action-button"
              disabled={isPaused || (isRequestingPause && !!pause?.pauseAgreedPlayerIds.includes(game.myPlayerId))}
              onClick={() => actions.pauseAction(isRequestingPause ? 'agree_pause' : 'request')}
              aria-label={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              title={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              data-tooltip={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
            >
              <Icon name="pause" size={14} />
            </button>
            <span
              className="self-player-avatar"
              style={{
                background: myAvatar.gradient,
                borderColor: myAvatar.borderColor,
                boxShadow: `0 0 8px ${myAvatar.glow}`,
              }}
              title={myPlayerName}
              aria-hidden="true"
            >
              {myAvatar.initial}
            </span>
            <span className="self-player-name">{myPlayerName}</span>
            <ReactionPicker onSelect={actions.sendReaction} onOpenProfile={onOpenProfile} direction="up" compact />

            {/* Muushig Specific Status: Score countdown */}
            <span
              className="self-score-badge"
              title={isMn ? `Оноо: ${myTotalScore} (0 болж түрүүлнэ)` : `Score: ${myTotalScore} (Target: 0)`}
            >
              <Icon name="star" size={12} className="seat-point-icon" />
              <strong>{myTotalScore}</strong>
            </span>

            {/* Tricks won - only during trick playing stage */}
            {muushig.stage === 'playing' && (
              <span
                className={`self-tricks-badge${myTricksWon > 0 ? ' is-safe' : ' is-danger'}`}
                title={
                  myTricksWon > 0
                    ? (isMn ? `Авсан гэр: ${myTricksWon}` : `Tricks won: ${myTricksWon}`)
                    : (isMn ? '0 гэр: +5 торгуулийн аюултай!' : '0 tricks: risk of +5 Muushig penalty!')
                }
              >
                {myTricksWon > 0 ? (
                  <Icon name="card-pile" size={13} className="seat-trick-icon" />
                ) : (
                  <span className="trick-danger-icon" aria-hidden="true">⚠️</span>
                )}
                <strong className="trick-count">{myTricksWon}</strong>
                <span>{isMn ? 'гэр' : 'tricks'}</span>
              </span>
            )}

            {/* Dealer badge only (avoids redundant 1-r gar, 2-r gar badges) */}
            {orderIndex === 0 && (
              <span
                className="hand-order-badge is-dealer"
                title={t.dealerRole}
              >
                👑 {t.dealerRole}
              </span>
            )}
          </div>
        </div>

        {/* Hand without auto-playable highlights */}
        <MyHand
          isPaused={isPaused}
          isMyTurn={isMyTurn}
          sortedHand={sortedHand}
          selectedCards={selectedCards}
          toggleCard={toggleCard}
          trumpSuit={muushig.trumpSuit}
          selectionDisabled={isFolded || isPaused || muushig.stage === 'decision'}
          emptyStateMessage={emptyNotice}
        />
      </div>
    </div>
    </TableInteractionsProvider>
  );
}
